import { bunja } from 'bunja';
import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { useEffect, useRef } from 'react';

import { T } from '@flipster/i18n';
import { TextButton } from '@flipster/react/design-system/text-button';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { hstack } from '@flipster/styled-system/patterns';

import { ClearableNumberInputWithFrame } from '../../../../input/clearable-number-input';
import { instrumentBunja } from '../../../state/base';
import { tpslFormSegmentBunja } from '../../segment';
import { tpslModalInputBunja, tpslModalInputDerivationBunja } from '../../state';

const percentPrecision = 2;

export const TriggerPriceInput = () => {
  const {
    pricePrecisionAtom,
    triggerPriceInputTextAtom,
    priceDiffFromEntryPriceAtom,
    onTriggerPriceInputChange,
    onFromEntryButtonClick,
  } = useBunja(triggerPriceInputBunja);
  const { trackTpslPriceClearClick, trackTpslPriceEditClick } = useBunja(tpslFormSegmentBunja);
  const t = useTranslations();
  const pricePrecision = useAtomValue(pricePrecisionAtom);
  const triggerPriceInputValue = useAtomValue(triggerPriceInputTextAtom);
  const priceDiffFromEntryPrice = useAtomValue(priceDiffFromEntryPriceAtom);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const rafId = requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    });
    return () => cancelAnimationFrame(rafId);
  }, []);

  return (
    <ClearableNumberInputWithFrame
      ref={inputRef}
      precision={pricePrecision}
      value={triggerPriceInputValue}
      onChange={onTriggerPriceInputChange}
      onFocus={trackTpslPriceEditClick}
      onClearClick={trackTpslPriceClearClick}
      placeholder={t('Trade.r250401.Enter_price')}
      customStyle
      className={css({
        p: 0,
        border: 'none',
        textStyle: 'Title/S/Monospace',
        color: 'content.default.level-5',
        caretColor: 'content.accent.info',
      })}
      suffix={
        <div className={hstack({ gap: 4, textStyle: 'Text/M/Monospace/Regular' })}>
          <Formatted.Percent
            withColor
            withPlus
            toFixed
            method='round-half-up'
            precision={percentPrecision}
            value={String(priceDiffFromEntryPrice)}
          />
          <TextButton
            onClick={onFromEntryButtonClick}
            label={<T id='Trade.r250401.From_entry' />}
            color='neutral'
            size='medium'
          />
        </div>
      }
    />
  );
};

const triggerPriceInputBunja = bunja(() => {
  const { pricePrecisionAtom } = bunja.use(instrumentBunja);
  const { triggerPriceInputTextAtom, setTriggerPriceInputText, setInputValueFromEntry } =
    bunja.use(tpslModalInputBunja);
  const { priceDiffFromEntryPriceAtom } = bunja.use(tpslModalInputDerivationBunja);
  const { trackTpslPriceResetClick } = bunja.use(tpslFormSegmentBunja);

  function onTriggerPriceInputChange(value: string): void {
    setTriggerPriceInputText(value, true);
  }

  function onFromEntryButtonClick() {
    trackTpslPriceResetClick();
    setInputValueFromEntry();
  }

  return {
    pricePrecisionAtom,
    triggerPriceInputTextAtom,
    priceDiffFromEntryPriceAtom,
    onTriggerPriceInputChange,
    onFromEntryButtonClick,
  };
});
triggerPriceInputBunja.debugLabel = 'triggerPriceInputBunja';
